"use client";
import { useMemo, useState } from "react";
import { Badge, badgeVariants } from "@/components/ui/badge";
import type { VariantProps } from "class-variance-authority";
import { Button } from "../button";
import { Skeleton } from "../skeleton";
import { ShoppingCart, ClipboardCopy } from "lucide-react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { toast } from "react-toastify";
import { useSession } from "next-auth/react";
import { Product } from "@/lib/models/product";
import type { DisplayPricing } from "@/lib/utils/generatePrice";
import { filterBrand } from "@/lib/utils/RedirectBrand";
import { RelatedProductSumbenu } from "./ProductSubmenu";
import { getDisplayPricing, renderCardPrices } from "@/lib/utils/generatePrice";
import { useDictionaryStore } from "@/lib/store/dictionaryStore";
import { useCart } from "@/lib/hooks/useCart";
import { useModal } from "@/lib/hooks/useModal";
import { ProductImageFallback } from "@/components/ui/ProductImageFallback";
import { CustomTooltip } from "../tooltip";
import { IntegratorCardButton } from "./IntegratorCardButton";
import { QuantitySelector } from "@/components/ui/QuantitySelector";
import { PriceRangeModalContent } from "@/components/ui/PriceRangeTable";
import { cn } from "@/lib/utils";
import {
  canSupplierViewBrand,
  canViewPrivilegedPrices,
  isAdminSession,
  isInternalSession,
  isSapLinkedSession,
  isSupplierSession,
  supplierPriceMessage,
  homePriceInvitationMessage,
} from "@/lib/utils/pricingVisibility";

interface ProductCardProps {
  product: Product;
  shorted?: boolean;
  recomended?: boolean;
  loadingPrices?: boolean;
  simpleCard?: boolean;
}

export const ProductCard = ({
  product,
  shorted = false,
  recomended = true,
  loadingPrices,
  simpleCard = false,
}: ProductCardProps) => {
  const { translationsType } = useDictionaryStore();
  const { data: session } = useSession();
  const { addItemToCart, formatPrice, productKey } = useCart();
  const { showModal, closeTopModal } = useModal();
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const hasPriceRanges = useMemo(
    () =>
      Boolean(
        product.price?.secciones?.some(
          (s) =>
            (s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_RANGOS" ||
              s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_SIN_RANGOS") &&
            (s.entradas?.length ?? 0) > 0,
        ),
      ),
    [product.price?.secciones],
  );

  const router = useRouter();

  // Variables auxiliares de estado
  const isAdmin = isAdminSession(session);
  const isLinked = isSapLinkedSession(session);
  const isSupplier = isSupplierSession(session);
  const isInternal = isInternalSession(session);
  const canViewSupplierBrand = canSupplierViewBrand(session, product.marcaCode);
  const canViewPrivilegedPrice = canViewPrivilegedPrices(
    session,
    product.marcaCode,
  );
  const canAccessProductActions =
    !isSupplier &&
    (canViewPrivilegedPrice || product.itemVentaDirecta !== false);
  const canViewStock =
    isAdmin ||
    isInternal ||
    isLinked ||
    isSupplier ||
    product.itemVentaDirecta !== false;
  const isDefaultCustomer =
    session === null || session === undefined || !canViewPrivilegedPrice;
  // Invitación a ver precios de integrador (invitados y clientes no vinculados),
  // solo en productos que no son de venta directa; `undefined` es la sesión aún
  // cargando.
  const showIntegratorCta =
    session !== undefined &&
    !canViewPrivilegedPrice &&
    !isSupplier &&
    product.itemVentaDirecta === false;
  // En la card compacta (altura fija) la imagen se encoge para que el botón de
  // integrador quepa completo.
  const hasFlexibleImage = simpleCard && showIntegratorCta;

  const displayPricing = useMemo(
    () => getDisplayPricing(product, isDefaultCustomer, productKey) || {},
    [product, isDefaultCustomer, productKey],
  );

  const notify = () =>
    toast.success("SKU copiado al portapapeles", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  async function handleCopySKU(sku: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(sku);
      notify();
    } else {
      toast.error(
        "No se pudo copiar el SKU. Tu navegador no soporta esta función.",
      );
    }
  }

  function showPrice(prices: DisplayPricing, customTextSizeClass?: string) {
    if (loadingPrices) {
      return (
        <>
          <Skeleton className="h-6 sm:h-7 md:h-8 w-24 sm:w-28 md:w-32 mb-1 sm:mb-2" />
          <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
        </>
      );
    }

    if (canViewPrivilegedPrice) {
      return (
        <div className="flex gap-1 sm:gap-2 md:gap-3 items-end w-full">
          {renderCardPrices(
            product,
            isDefaultCustomer,
            productKey,
            formatPrice,
            customTextSizeClass ||
              "text-base sm:text-lg md:text-lg lg:text-xl xl:text-2xl",
          )}
        </div>
      );
    }

    if (isSupplier) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {supplierPriceMessage}
        </span>
      );
    }

    // 2. Si el producto NO es de venta directa, mostramos mensaje de invitación
    if (product.itemVentaDirecta === false) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {homePriceInvitationMessage}
        </span>
      );
    }

    // 3. Producto de venta directa para usuarios no vinculados o invitados
    if ((product.itemVentaDirecta as boolean | undefined) !== false) {
      return (
        <div className="flex gap-1 sm:gap-2 md:gap-3 items-end w-full">
          {renderCardPrices(
            product,
            isDefaultCustomer,
            productKey,
            formatPrice,
            customTextSizeClass ||
              "text-base sm:text-lg md:text-lg lg:text-xl xl:text-2xl",
          )}
        </div>
      );
    }

    // 4. Fallback si no hay sesión y no es venta directa
    if (session === null) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {homePriceInvitationMessage}
        </span>
      );
    }

    return null;
  }
  const totalStock =
    product.price?.stockDisponibleTotal ??
    product.stockDisponibleTotal ??
    product.stockTotal ??
    0;

  const handleOpenPriceRanges = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    showModal({
      title: "Precios por volumen",
      content: (
        <PriceRangeModalContent
          product={product}
          initialQuantity={quantity}
          totalStock={totalStock}
          onAddToCart={(finalQty) => {
            setQuantity(finalQty);
            const pricing = displayPricing as DisplayPricing;
            const unitPrice = pricing?.mainPrice || product?.priceList1 || 0;
            addItemToCart(product, unitPrice, finalQty);
            closeTopModal();
          }}
        />
      ),
      size: "xl",
    });
  };

  const getTranslation = (key: string) => {
    const type = translationsType.find((item) => item.name === key);
    return type?.description || key;
  };

  const getBadgeVariantForType = (
    type: string,
  ): VariantProps<typeof badgeVariants>["variant"] => {
    switch (type) {
      case "PDP_RECOMMENDED":
        return "solidGreen";
      case "PDP_ALTERNATIVES":
        return "solidBlue";
      case "PDP_ACCESSORIES":
        return "solidOrange";
      case "PDP_UPGRADES":
        return "solidAmber";
      case "PDP_YOU_NEED":
        return "solidRed";
      case "PDP_FREQUENTLY_BOUGHT":
        return "solidIndigo";
      default:
        return "solidBlue";
    }
  };

  const isFeaturedAlternative =
    product.type === "PDP_ALTERNATIVES" && product.isFeatured;

  return (
    <Link
      href={`/producto/${product.slug}`}
      className={cn(
        isFeaturedAlternative
          ? "bg-gradient-to-b from-amber-50/20 to-white border-2 border-amber-300 rounded-lg shadow-md hover:shadow-2xl hover:border-amber-400 transition-all duration-300 w-full relative flex flex-col h-full cursor-pointer ring-1 ring-amber-300/30 group"
          : "bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300 w-full relative flex flex-col h-full cursor-pointer group",
        showSubmenu ? "z-40" : "z-0",
      )}
      onClick={(e) => {
        const target = e.target as Element;
        const innerLink = target.closest("a");
        const isInnerLink = innerLink && innerLink !== e.currentTarget;
        const isInteractive =
          target.closest("button") ||
          target.closest(".no-navigate") ||
          target.closest("[data-no-navigate]") ||
          isInnerLink;

        if (isInteractive || e.defaultPrevented) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        if (
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.altKey ||
          e.shiftKey
        ) {
          return;
        }

        e.preventDefault();
        router.push(`/producto/${product.slug}`);
      }}
    >
      {/* Image Container */}
      <div
        className={cn(
          "relative bg-gray-50 p-0 rounded-t-lg",
          hasFlexibleImage && "flex min-h-0 flex-1 flex-col",
        )}
      >
        {/* Top Header Overlay */}
        <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-1.5 z-30 pointer-events-none">
          {/* Badges Stack */}
          <div className="flex flex-col gap-1 items-start min-w-0 flex-1">
            {product?.price?.secciones?.some(
              (s) => s.tipo === "PRECIO_LIQUIDACION",
            ) && (
              <Badge variant="danger" className="whitespace-nowrap text-[11px]">
                Liquidación
              </Badge>
            )}
            {(displayPricing as DisplayPricing)?.hasDiscount && (
              <Badge variant="orange" className="whitespace-nowrap text-[11px]">
                Promoción
              </Badge>
            )}
            {product?.isNew && (
              <Badge
                variant="success"
                className="whitespace-nowrap text-[11px]"
              >
                Producto nuevo
              </Badge>
            )}
            {product?.isFeatured && (
              <Badge
                variant="warning"
                className="whitespace-nowrap text-[11px]"
              >
                Producto destacado
              </Badge>
            )}
            {product?.type && (
              <Badge
                variant={getBadgeVariantForType(product.type)}
                className="whitespace-nowrap text-[11px]"
              >
                {getTranslation(product?.type as string)}
              </Badge>
            )}
          </div>

          {/* Action Buttons */}
          {!simpleCard && (
            <div className="flex items-center gap-1.5 no-navigate pointer-events-auto shrink-0 ml-auto">
              {/* Botón Ver Más */}
              <RelatedProductSumbenu
                isOpen={showSubmenu}
                setShowSubmenu={(show: boolean) => setShowSubmenu(show)}
                product={product}
                recomended={recomended}
                onClose={() => setShowSubmenu(false)}
                triggerClassName="cursor-pointer bg-white p-1 sm:p-1 shadow-xs hover:bg-gray-50 transition-colors flex items-center justify-center rounded-md border border-slate-100"
              />
            </div>
          )}
        </div>

        <div
          className={`flex justify-center items-center py-0 w-full ${
            hasFlexibleImage
              ? "min-h-20 flex-1"
              : simpleCard
                ? "h-48 sm:h-52"
                : "h-65"
          } bg-white rounded-t-lg overflow-hidden`}
        >
          <ProductImageFallback
            urlMainImg={product.urlMainImg}
            alt={product?.name || `Fotografía del producto SKU ${product.sku}`}
            className="w-full h-full object-contain"
            iconSize={simpleCard ? 64 : 80}
          />
        </div>
      </div>

      <div
        className={`p-2.5 sm:p-3 flex flex-col ${
          hasFlexibleImage
            ? "flex-none"
            : simpleCard
              ? "flex-1 justify-between"
              : "h-full"
        }`}
      >
        <div className="flex-1">
          {product?.name && product.name.length > 20 ? (
            <CustomTooltip
              content={product.name}
              className="max-w-[220px] break-words text-center"
            >
              <span className="text-xs sm:text-sm md:text-sm text-gray-900 mb-1 sm:mb-1.5 line-clamp-2 leading-snug font-medium">
                {product.name}
              </span>
            </CustomTooltip>
          ) : (
            <span className="text-xs sm:text-sm md:text-sm text-gray-900 mb-1 sm:mb-1.5 line-clamp-2 leading-snug font-medium">
              {product?.name}
            </span>
          )}
        </div>
        {!simpleCard && (
          <div className="w-full grid grid-cols-5 gap-5 items-center">
            <p className="text-xs text-gray-400 col-span-4 flex items-center gap-2">
              {product.sku}
              <Button
                className="!flex !justify-end !p-0 hover:!bg-white transition-colors no-navigate"
                aria-label={`Copiar código SKU ${product.sku}`}
                tooltip="Copiar SKU"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  handleCopySKU(product.sku);
                }}
              >
                <ClipboardCopy className="text-primary size-4 hover:text-secondary" />
              </Button>
            </p>
          </div>
        )}
        {canViewStock && (
          <p
            className={
              simpleCard
                ? "text-[11px] sm:text-xs font-semibold text-slate-500 mt-1"
                : "text-md font-semibold text-gray-500 col-span-6"
            }
          >
            Stock disponible:{" "}
            <span className="font-bold text-slate-700">{totalStock}</span>
          </p>
        )}

        {simpleCard && (
          <div className="mt-auto pt-1 min-h-[28px] flex items-center">
            {/* Sin precio visible, el botón de integrador reemplaza al mensaje */}
            {!showIntegratorCta &&
              showPrice(
                displayPricing as DisplayPricing,
                "text-sm sm:text-base font-bold",
              )}
          </div>
        )}

        {simpleCard && showIntegratorCta && (
          <div className="mt-2 w-full">
            <IntegratorCardButton isGuest={session === null} />
          </div>
        )}

        {!simpleCard && (
          <>
            <div className="mt-auto mb-1 sm:mb-2 md:mb-1 min-h-[40px]">
              {/* Sin precio visible, el botón de integrador reemplaza al mensaje */}
              {!showIntegratorCta &&
                showPrice(displayPricing as DisplayPricing)}
            </div>

            {product?.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 mb-1 sm:mb-1 md:mb-1 no-navigate">
                {!showAllTags
                  ? product.tags.slice(0, 4).map((tag, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="cursor-pointer !text-[11px]"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          router.push(
                            `/productos?tags=["${encodeURIComponent(tag)}"]`,
                          );
                        }}
                      >
                        {tag}
                      </Badge>
                    ))
                  : product.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="cursor-pointer !text-[11px]"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          router.push(
                            `/productos?tags=["${encodeURIComponent(tag)}"]`,
                          );
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                {product.tags.length > 4 && (
                  <Badge
                    variant="default"
                    className="cursor-pointer !text-[11px]"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAllTags(!showAllTags);
                    }}
                  >
                    {showAllTags
                      ? "Mostrar menos"
                      : `+${product.tags.length - 4}`}
                  </Badge>
                )}
              </div>
            )}

            {showIntegratorCta && (
              <div className="mt-1 mb-1 w-full">
                <IntegratorCardButton isGuest={session === null} />
              </div>
            )}

            {/* Footer */}
            <div className="pt-1.5 sm:pt-2 mt-auto no-navigate flex flex-col gap-1.5">
              <span
                className="text-primary text-xs truncate cursor-pointer hover:text-primary/70 font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  filterBrand(product.marcaCode);
                }}
              >
                {product.marcaName}
              </span>
              {canAccessProductActions && (
                <div className="flex items-center gap-1.5 w-full">
                  <QuantitySelector
                    quantity={quantity}
                    min={1}
                    max={totalStock > 0 ? totalStock : 1}
                    onChange={setQuantity}
                    disabled={totalStock <= 0}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    data-no-navigate="true"
                    className="text-xs shrink-0 h-8 px-2.5 sm:px-3 flex items-center justify-center gap-1.5 no-navigate shadow-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (hasPriceRanges) {
                        handleOpenPriceRanges(e);
                        return;
                      }

                      const pricing = displayPricing as DisplayPricing;
                      const unitPrice =
                        pricing?.mainPrice || product?.priceList1 || 0;

                      addItemToCart(product, unitPrice, quantity);
                    }}
                    disabled={totalStock <= 0}
                    aria-label="Agregar al carrito"
                  >
                    <ShoppingCart size={15} />
                    {!shorted && <span className="text-xs">Agregar</span>}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Link>
  );
};
